import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SupplierUnitOfWork } from '@data';
import { AccountAdmin, AccountUser, ProximityRightEnum, SupplierAdmin } from '@models';
import { AuthService, Roles } from '../../services/auth.service';
import { UtilDialogService } from '../../services/dialog.service';
import { CoreAccountFinderDialog } from '../user/core-account-finder.dialog';
import { CoreSupplierFinderDialog } from '../user/core-supplier-finder.dialog';
import { CoreUserFinderDialog } from '../user/core-user-finder.dialog';
import { ImpersonateDialogComponent } from './impersonate-dialog.component';
import { MessageBusService, MessageTypeEnum } from '../../services/message-bus.service';

@Injectable({ providedIn: 'root' })
export class ImpersonationService {
  constructor(
    private uow: SupplierUnitOfWork,
    private authService: AuthService,
    private dialogService: UtilDialogService,
    private messageBusService: MessageBusService,
    private router: Router,
  ) {}

  async openDialog() {
    let ref: any;
    return new Promise((resolve) => {
      ref = this.dialogService.pngDialogService.open(ImpersonateDialogComponent, {
        data: { impersonationService: this },
        header: 'Impersonate User',
        width: '35rem',
        contentStyle: { height: '20rem' },
        draggable: true,
        closable: true,
        modal: false,
        resizable: true,
      });

      if (!ref) {
        resolve(true);
      }
      ref.onClose.subscribe(() => {
        resolve(true);
      });
    });
  }

  canImpersonateAccountUser() {
    const user = this.authService.getUser();
    return (
      (user?.hasOrigRole(Roles.GlobalAdmin) ||
        user?.hasRight(ProximityRightEnum.CanManageAccountAdministration, ProximityRightEnum.CanImpersonateUsersByApprovalTree)) &&
      !window.location.href.match(ImpersonationService.supplierIdRegex)
    );
  }

  canImpersonateAccountAdmin() {
    const user = this.authService.getUser();
    return (
      (user?.hasOrigRole(Roles.GlobalAdmin) || user?.hasRight(ProximityRightEnum.CanManageAccountAdministration)) &&
      !window.location.href.match(ImpersonationService.supplierIdRegex)
    );
  }

  canImpersonateSupplierAdmin() {
    return this.authService.getUser()?.hasOrigRole(Roles.GlobalAdmin) && !window.location.href.match(ImpersonationService.accountIdRegex);
  }

  async impersonateAccountUser(): Promise<void> {
    const accountId = await this.getAccountId();
    if (accountId) {
      const allAccountUsers = await this.uow
        .createQuery(AccountUser)
        .expand(['proximityUser.proximityRole', 'proximityUser.activeStatus'])
        .where({ accountId })
        .execute();
      return this.selectUser(allAccountUsers, 'Select an Account User', '/account-user/place-order-card');
    }
  }

  async impersonateAccountAdmin(): Promise<void> {
    const accountId = await this.getAccountId();
    if (accountId) {
      const allAccountUsers = await this.uow
        .createQuery(AccountAdmin)
        .expand(['proximityUser.proximityRole', 'proximityUser.activeStatus'])
        .where({ accountId })
        .execute();
      // Land on the account-admin home tile page, not the /manage subroute — that
      // subroute redirects to approvals which fails for restricted admin variants
      // (OrderAdmin, SupportAdmin) that lack CanApproveJobOrderByApprovalTree.
      return this.selectUser(allAccountUsers, 'Select an Account Administrator', `/account-admin/home/${accountId}`);
    }
  }

  async impersonateSupplierAdmin(): Promise<void> {
    const supplierId = await this.getSupplierId();
    if (supplierId) {
      const allSupplierUsers = await this.uow
        .createQuery(SupplierAdmin)
        .expand(['proximityUser.proximityRole', 'proximityUser.activeStatus'])
        .where({ supplierId })
        .execute();
      return this.selectUser(allSupplierUsers, 'Select a Supplier Administrator');
    }
  }

  /** If current user belongs to an account, return that accountId; else show an account finder dialog */
  private async getAccountId() {
    let accountId = this.authService.getUser()?.accountId;
    if (!accountId) {
      accountId = this.getAccountIdFromUrl();
    }
    if (!accountId) {
      const accounts = await this.dialogService.createFinder(CoreAccountFinderDialog, {});
      if (accounts.length) {
        accountId = accounts[0].id;
      }
    }
    return accountId;
  }

  /** If current user belongs to a supplier, return that supplierId; else show an supplier finder dialog */
  private async getSupplierId() {
    let supplierId = this.authService.getUser()?.supplierId;
    if (!supplierId) {
      supplierId = this.getSupplierIdFromUrl();
    }
    if (!supplierId) {
      const suppliers = await this.dialogService.createFinder(CoreSupplierFinderDialog, {});
      if (suppliers.length) {
        supplierId = suppliers[0].id;
      }
    }
    return supplierId;
  }

  private static accountIdRegex = /\/account(?:-user|-admin)?\/(?:accounts(?:-for-users)?|dashboard)\/([a-f0-9-]{36})/;
  private static supplierIdRegex = /\/supplier\/suppliers\/([a-f0-9-]{36})/;
  private getAccountIdFromUrl() {
    const match = window.location.href.match(ImpersonationService.accountIdRegex);
    const id = match ? match[1] : undefined;
    return id;
  }
  private getSupplierIdFromUrl() {
    const match = window.location.href.match(ImpersonationService.supplierIdRegex);
    const id = match ? match[1] : undefined;
    return id;
  }

  private async selectUser(allUsers: any[], title?: string, navigateTo?: string) {
    const selectedUsers = await this.dialogService.createFinder(CoreUserFinderDialog, {
      users: allUsers,

      title: title,
    });
    if (selectedUsers.length) {
      await this.authService.impersonateUser(selectedUsers[0].proximityUserId);

      if (navigateTo) {
        await this.router.navigateByUrl(navigateTo);
        location.reload();
      } else {
        location.reload();
      }
    }
  }

  stopImpersonation(): void {
    this.authService.stopImpersonation();
  }
}
